import { useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { FiCalendar, FiDollarSign, FiHome, FiTrendingDown, FiTrendingUp } from 'react-icons/fi';
import { AppLayout } from '../components/AppLayout';
import { Card } from '../components/Card';
import { Kpi } from '../components/Kpi';
import { useAuth } from '../context/AuthContext';
import { buscarRelatorios } from '../api/relatorios';
import { Relatorios } from '../types';
import { formatarData, formatarMoeda } from '../utils/formatadores';

export function Home() {
  const { usuario } = useAuth();
  const [dados, setDados] = useState<Relatorios | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    buscarRelatorios()
      .then(setDados)
      .finally(() => setCarregando(false));
  }, []);

  const dadosGrafico = useMemo(() => {
    if (!dados) return [];
    const porData = new Map<string, { data: string; entradas: number; saidas: number }>();
    for (const lancamento of dados.financeiro.por_dia ?? []) {
      const chave = lancamento.data;
      const atual = porData.get(chave) ?? { data: chave, entradas: 0, saidas: 0 };
      if (lancamento.tipo === 'entrada') atual.entradas += lancamento.total;
      else atual.saidas += lancamento.total;
      porData.set(chave, atual);
    }
    return Array.from(porData.values())
      .sort((a, b) => a.data.localeCompare(b.data))
      .map((item) => ({ ...item, dataFormatada: formatarData(item.data) }));
  }, [dados]);

  return (
    <AppLayout>
      <div className="lizzi-cabecalho-pagina">
        <h1>
          <span className="lizzi-icone-titulo">
            <FiHome size={18} />
          </span>
          Olá, {usuario?.nome}!
        </h1>
      </div>
      <p style={{ fontSize: '14px', color: 'var(--cor-texto)', opacity: 0.75, marginTop: '-12px', marginBottom: '24px' }}>
        Resumo financeiro do seu restaurante nos últimos 30 dias.
      </p>

      {carregando || !dados ? (
        <p>Carregando painel...</p>
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '16px',
              marginBottom: '24px',
            }}
          >
            <Kpi titulo="Entradas" valor={formatarMoeda(dados.financeiro.total_entradas)} tone="sucesso" icone={<FiTrendingUp size={20} />} />
            <Kpi titulo="Saídas" valor={formatarMoeda(dados.financeiro.total_saidas)} tone="erro" icone={<FiTrendingDown size={20} />} />
            <Kpi
              titulo="Saldo"
              valor={formatarMoeda(dados.financeiro.saldo)}
              tone={dados.financeiro.saldo >= 0 ? 'sucesso' : 'erro'}
              icone={<FiDollarSign size={20} />}
            />
            <Kpi
              titulo="Melhor dia de entrada"
              valor={dados.financeiro.melhor_dia_entrada ? formatarMoeda(dados.financeiro.melhor_dia_entrada.total) : 'Sem dados'}
              tone="dourado"
              icone={<FiCalendar size={20} />}
              detalhe={dados.financeiro.melhor_dia_entrada ? formatarData(dados.financeiro.melhor_dia_entrada.data) : undefined}
            />
          </div>

          <Card>
            <h2 style={{ fontSize: '16px', marginBottom: '16px' }}>Entradas x saídas por dia (últimos 30 dias)</h2>
            {dadosGrafico.length === 0 ? (
              <p style={{ fontSize: '14px', opacity: 0.7 }}>Nenhum lançamento financeiro nos últimos 30 dias.</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dadosGrafico}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--cor-borda)" />
                  <XAxis dataKey="dataFormatada" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip formatter={(valor) => formatarMoeda(Number(valor))} />
                  <Legend />
                  <Bar dataKey="entradas" name="Entradas" fill="#1e9e58" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="saidas" name="Saídas" fill="#e0293e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </>
      )}
    </AppLayout>
  );
}
